import { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Mail, Mic } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { pdfDownloadUrl } from "@/lib/pdfDownload";

const Connect = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "subscribe-newsletter",
        { body: { email, source: "connect_page" } },
      );
      if (error || (data && (data as any).error)) {
        throw error ?? new Error((data as any).error);
      }

      toast({
        title: "You're in.",
        description: "Welcome.",
      });
      setEmail("");
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Connect | Rob Lasa"
        description="If something here landed — or if you just need to say something out loud to someone — I'm here."
        keywords="contact rob lasa, connect, speaking, podcast guest"
        url="/connect"
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-glow opacity-50" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-in">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-primary-foreground">
              Say <span className="text-accent">Something</span>
            </h1>
            <p className="text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              If something here landed — or if you just need to say something out loud to someone — I'm here.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-12">
            {/* Direct Contact */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    You can also reach me directly via <a href="https://www.instagram.com/souldadrob/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instagram DM<span className="sr-only"> (opens in a new tab)</span></a> or email.
                  </p>
                  <a 
                    href="mailto:rob@souldadrob.com"
                    className="text-sm text-primary hover:underline cursor-pointer"
                  >
                    rob@souldadrob.com
                  </a>
                </div>
              </div>
            </Card>

            {/* Speaking */}
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Mic className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Speaking & Media</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Available for podcast appearances, events, and conversations about the nice guy trap, men's mental health, and what happens when you stop performing.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href="mailto:rob@souldadrob.com?subject=Speaking%20%2F%20Media%20Inquiry">
                      Reach Out
                    </a>
                  </Button>
                </div>
              </div>
            </Card>

            {/* Subscribe */}
            <Card className="p-8">
              <h2 className="text-2xl font-display font-bold mb-2">Stay in Touch</h2>
              <p className="text-muted-foreground mb-6">
                Occasional reflections and journal entries — delivered to your inbox. No spam. No formulas. Just honest words.
              </p>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-accent text-white hover:bg-accent/90 whitespace-nowrap"
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </Button>
              </form>
            </Card>

            {/* Free resources */}
            <div className="pt-4">
              <p className="text-sm text-muted-foreground mb-3">
                While you're here — a couple of things I wrote that might land.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={pdfDownloadUrl("nice-vs-kind", "connect")} rel="noopener" className="text-sm text-primary hover:underline">
                  Nice vs. Kind →
                </a>
                <a href={pdfDownloadUrl("you-cant-give-what-you-dont-have", "connect")} rel="noopener" className="text-sm text-primary hover:underline">
                  You Can't Give What You Don't Have →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Connect;
